import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCalendarConnections } from '../../../features/calendarSlice'
import { setSettingsTab } from '../../../features/authSlice'
import {
	CALENDAR_PROVIDERS,
	isProviderConnected,
	getDefaultSyncFromConnections,
} from '../../../util/calendarProviders'
import ButtonCustom from '../button/ButtonCustom.jsx'
import styles from './CalendarSync.module.scss'

const CALENDAR_INTEGRATIONS_TAB_INDEX = 3

export const CalendarSync = ({
	value,
	onChange,
	formMode = false,
	defaultValues,
	showLabel = true,
	className = '',
	labelClassName = '',
}) => {
	const dispatch = useDispatch()
	const connections = useSelector((state) => state.calendar.connections)

	const reduxDefault = useMemo(
		() => getDefaultSyncFromConnections(connections),
		[connections]
	)

	const effectiveDefault = defaultValues ?? reduxDefault

	const isControlled = value !== undefined && typeof onChange === 'function'
	const [formState, setFormState] = useState(() => ({
		google: effectiveDefault?.google === true,
		outlook: effectiveDefault?.outlook === true,
		ical: effectiveDefault?.ical === true,
	}))

	const current = isControlled ? (value ?? reduxDefault) : formState
	const [connectPromptFor, setConnectPromptFor] = useState(null)

	useEffect(() => {
		const fetchConnections = () => {
		  dispatch(getCalendarConnections())
		}
	  
		fetchConnections()
	  
		window.addEventListener('focus', fetchConnections)
	  
		return () => window.removeEventListener('focus', fetchConnections)
	  }, [dispatch])

	useEffect(() => {
		if (formMode && !isControlled) {
			setFormState({
				google: effectiveDefault?.google === true,
				outlook: effectiveDefault?.outlook === true,
				ical: effectiveDefault?.ical === true,
			})
		}
	}, [formMode, isControlled, effectiveDefault?.google, effectiveDefault?.outlook, effectiveDefault?.ical])

	const handleToggle = useCallback(
		(providerId) => {
			if (!isProviderConnected(connections, providerId)) {
				setConnectPromptFor(providerId)
				return
			}
			setConnectPromptFor(null)
			const next = {
				...current,
				[providerId]: !current[providerId],
			}
			if (isControlled) {
				onChange(next)
			} else {
				setFormState(next)
			}
		},
		[connections, current, isControlled, onChange]
	)

	const handleNeedConnect = useCallback(() => {
		dispatch(setSettingsTab(CALENDAR_INTEGRATIONS_TAB_INDEX))
		const url = `/settings?tab=${CALENDAR_INTEGRATIONS_TAB_INDEX}`
		window.open(url, '_blank', 'noopener,noreferrer')
	}, [dispatch])

	const connectPromptProvider = useMemo(() => {
		if (!connectPromptFor) return null
		return CALENDAR_PROVIDERS.find((p) => p.id === connectPromptFor) ?? null
	}, [connectPromptFor])

	const shouldShowConnectPrompt = useMemo(() => {
		if (!connectPromptProvider) return false
		return !isProviderConnected(connections, connectPromptProvider.id)
	}, [connections, connectPromptProvider])

	return (
		<div className={`${styles.wrapper} ${className}`}>
			{showLabel && (
				<label className={`${styles.label} ${labelClassName}`}>
					Calendar Sync
				</label>
			)}
			{formMode &&
				CALENDAR_PROVIDERS.map(({ id, formName }) => (
					<input
						key={formName}
						type="checkbox"
						name={formName}
						checked={current[id] === true}
						onChange={() => handleToggle(id)}
						className={`e-field ${styles.hiddenCheckbox}`}
						aria-hidden
						tabIndex={-1}
					/>
				))}
			<div className={styles.buttonsRow}>
				{CALENDAR_PROVIDERS.map(({ id, label, logo }) => {
					const connected = isProviderConnected(connections, id)
					return (
						<ButtonCustom
							key={id}
							type="button"
							variant="outlined"
							size="sm"
							active={connected ? current[id] === true : false}
							onClick={() => handleToggle(id)}
							className={styles.button}
						>
							<img src={logo} alt="" aria-hidden />
							{label}
						</ButtonCustom>
					)
				})}
			</div>

			{shouldShowConnectPrompt && (
				<div className={styles.validationBox}>
					<p className={styles.validationText}>
						Not connected.
					</p>
					<ButtonCustom
						type="button"
						variant="default"
						size="sm"
						className={styles.connectNowBtn}
						onClick={handleNeedConnect}
					>
						Connect now
					</ButtonCustom>
				</div>
			)}
		</div>
	)
}

export default CalendarSync
